/**
 * post-V9 Section 0 Iter 3 — compact stores for TEXT and MTEXT.
 */

import type {
  AnnotScaleContext,
  DrawingPoint3D,
  MtextEntity,
  TextEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  BASE_BYTES_PER_ENTITY,
  createBaseColumns,
  growBaseColumns,
  readBase,
  writeBase,
  type BaseColumns,
} from "./compactStoreBase";
import {
  internString,
  lookupString,
  type SharedStringTable,
} from "./sharedStringTable";

/* ── TEXT ── */

const TEXT_FLOATS = 6;
const TEXT_INTS = 4;

export interface CompactTextStore {
  count: number;
  base: BaseColumns;
  floats: Float32Array;
  thicknesses: Float32Array;
  ints: Uint16Array;
  flags: Uint8Array;
  alignmentPoints: Map<number, { x: number; y: number }>;
  extrusions: Map<number, DrawingPoint3D>;
  annotativeContexts: Map<number, AnnotScaleContext[]>;
  creationScaleNames: Map<number, string>;
}

const DEFAULT_EXTRUSION_3D: DrawingPoint3D = { x: 0, y: 0, z: 1 };

function isDefaultExtrusion(e: DrawingPoint3D | undefined): boolean {
  if (!e) return true;
  return e.x === 0 && e.y === 0 && e.z === 1;
}

export function createCompactTextStore(
  initialCapacity = 256,
): CompactTextStore {
  const cap = Math.max(1, initialCapacity);
  return {
    count: 0,
    base: createBaseColumns(cap),
    floats: new Float32Array(cap * TEXT_FLOATS),
    thicknesses: new Float32Array(cap),
    ints: new Uint16Array(cap * TEXT_INTS),
    flags: new Uint8Array(cap),
    alignmentPoints: new Map(),
    extrusions: new Map(),
    annotativeContexts: new Map(),
    creationScaleNames: new Map(),
  };
}

function ensureTextCapacity(store: CompactTextStore, needed: number): void {
  if (needed <= store.base.capacity) return;
  const newCap = growBaseColumns(store.base, needed);
  const f = new Float32Array(newCap * TEXT_FLOATS);
  f.set(store.floats);
  const t = new Float32Array(newCap);
  t.set(store.thicknesses);
  const i = new Uint16Array(newCap * TEXT_INTS);
  i.set(store.ints);
  const fl = new Uint8Array(newCap);
  fl.set(store.flags);
  store.floats = f;
  store.thicknesses = t;
  store.ints = i;
  store.flags = fl;
}

export function appendText(
  store: CompactTextStore,
  table: SharedStringTable,
  entity: TextEntity,
): number {
  const index = store.count;
  ensureTextCapacity(store, index + 1);
  store.count++;
  const f = index * TEXT_FLOATS;
  store.floats[f + 0] = entity.insertionPoint.x;
  store.floats[f + 1] = entity.insertionPoint.y;
  store.floats[f + 2] = entity.height;
  store.floats[f + 3] = entity.rotation;
  store.floats[f + 4] = entity.widthFactor;
  store.floats[f + 5] = entity.obliqueAngle;
  store.thicknesses[index] = entity.thickness;
  const ii = index * TEXT_INTS;
  store.ints[ii + 0] = internString(table, entity.styleName);
  store.ints[ii + 1] = internString(table, entity.contents);
  store.ints[ii + 2] = entity.hJustification & 0xffff;
  store.ints[ii + 3] = entity.vJustification & 0xffff;
  store.flags[index] = entity.generationFlags & 0xff;
  writeBase(store.base, table, index, entity);

  if (entity.alignmentPoint !== undefined) {
    store.alignmentPoints.set(index, { ...entity.alignmentPoint });
  }
  if (!isDefaultExtrusion(entity.extrusion)) {
    store.extrusions.set(index, { ...entity.extrusion });
  }
  if (entity.annotativeContexts.length > 0) {
    store.annotativeContexts.set(
      index,
      entity.annotativeContexts.map((c) => ({ ...c })),
    );
  }
  if (entity.creationScaleName !== undefined) {
    store.creationScaleNames.set(index, entity.creationScaleName);
  }
  return index;
}

export function textFromCompact(
  store: CompactTextStore,
  table: SharedStringTable,
  index: number,
): TextEntity {
  if (index < 0 || index >= store.count) {
    throw new RangeError(
      `textFromCompact: index ${index} out of range (count=${store.count})`,
    );
  }
  const f = index * TEXT_FLOATS;
  const ii = index * TEXT_INTS;
  const result: TextEntity = {
    ...readBase(store.base, table, index),
    type: "Text",
    insertionPoint: { x: store.floats[f + 0]!, y: store.floats[f + 1]! },
    height: store.floats[f + 2]!,
    rotation: store.floats[f + 3]!,
    widthFactor: store.floats[f + 4]!,
    obliqueAngle: store.floats[f + 5]!,
    thickness: store.thicknesses[index]!,
    styleName: lookupString(table, store.ints[ii + 0]!),
    contents: lookupString(table, store.ints[ii + 1]!),
    hJustification: store.ints[ii + 2]!,
    vJustification: store.ints[ii + 3]!,
    generationFlags: store.flags[index]!,
    extrusion: store.extrusions.get(index) ?? { ...DEFAULT_EXTRUSION_3D },
    annotativeContexts: store.annotativeContexts.get(index) ?? [],
  };
  const align = store.alignmentPoints.get(index);
  if (align !== undefined) result.alignmentPoint = align;
  const csn = store.creationScaleNames.get(index);
  if (csn !== undefined) result.creationScaleName = csn;
  return result;
}

export function estimateTextBytesPerEntity(): number {
  return TEXT_FLOATS * 4 + 4 + TEXT_INTS * 2 + 1 + BASE_BYTES_PER_ENTITY;
}

/* ── MTEXT ── */

const MTEXT_FLOATS = 8;
const MTEXT_INTS = 4;
const MTEXT_BG_FLOATS = 1;
const MTEXT_BG_INTS = 4;
const FLAG_BG = 1;
const FLAG_COL_FLOW = 2;
const FLAG_COL_AUTO = 4;

export interface CompactMtextStore {
  count: number;
  base: BaseColumns;
  floats: Float32Array;
  ints: Uint16Array;
  bgFloats: Float32Array;
  bgInts: Uint16Array;
  flags: Uint8Array;
  backgroundFillColors: Map<number, string>;
  backgroundFillColorRGBs: Map<number, number>;
  columnCounts: Map<number, number>;
  columnHeights: Map<number, number[]>;
  lineSpacingStyles: Map<number, number>;
  xAxisDirections: Map<number, DrawingPoint3D>;
  // Codex #342 round-6 P1: MTEXT carries an OPTIONAL OCS extrusion (group
  // 210/220/230) that the MIRRTEXT=1 mirror sets to (0,0,−1) to record the
  // det=−1 handedness (`mirrorTextFull` in command-engine). The compact store
  // is the read-through cache that dispatch commits updated/added MTEXT into
  // BEFORE the export wraps the document, so without persisting the extrusion a
  // mirrored MTEXT materialised from the cache would save/reload as an ordinary
  // 180° rotation — losing the mirror. Map-backed + absent-stays-absent (NOT a
  // defaulted (0,0,1)) so a non-mirrored MTEXT round-trips with no group 210,
  // exactly like the schema's `.optional()` extrusion. Mirrors the TEXT store's
  // existing `extrusions` slot (same one-truth `isDefaultExtrusion` predicate).
  extrusions: Map<number, DrawingPoint3D>;
  horizontalWidths: Map<number, number>;
  verticalHeights: Map<number, number>;
  annotativeContexts: Map<number, AnnotScaleContext[]>;
  creationScaleNames: Map<number, string>;
}

const DEFAULT_X_AXIS: DrawingPoint3D = { x: 1, y: 0, z: 0 };

function isDefaultXAxis(p: DrawingPoint3D | undefined): boolean {
  if (!p) return true;
  return p.x === 1 && p.y === 0 && p.z === 0;
}

export function createCompactMtextStore(
  initialCapacity = 256,
): CompactMtextStore {
  const cap = Math.max(1, initialCapacity);
  return {
    count: 0,
    base: createBaseColumns(cap),
    floats: new Float32Array(cap * MTEXT_FLOATS),
    ints: new Uint16Array(cap * MTEXT_INTS),
    bgFloats: new Float32Array(cap * MTEXT_BG_FLOATS),
    bgInts: new Uint16Array(cap * MTEXT_BG_INTS),
    flags: new Uint8Array(cap),
    backgroundFillColors: new Map(),
    backgroundFillColorRGBs: new Map(),
    columnCounts: new Map(),
    columnHeights: new Map(),
    lineSpacingStyles: new Map(),
    xAxisDirections: new Map(),
    extrusions: new Map(),
    horizontalWidths: new Map(),
    verticalHeights: new Map(),
    annotativeContexts: new Map(),
    creationScaleNames: new Map(),
  };
}

function ensureMtextCapacity(store: CompactMtextStore, needed: number): void {
  if (needed <= store.base.capacity) return;
  const newCap = growBaseColumns(store.base, needed);
  const f = new Float32Array(newCap * MTEXT_FLOATS);
  f.set(store.floats);
  const i = new Uint16Array(newCap * MTEXT_INTS);
  i.set(store.ints);
  const bf = new Float32Array(newCap * MTEXT_BG_FLOATS);
  bf.set(store.bgFloats);
  const bi = new Uint16Array(newCap * MTEXT_BG_INTS);
  bi.set(store.bgInts);
  const fl = new Uint8Array(newCap);
  fl.set(store.flags);
  store.floats = f;
  store.ints = i;
  store.bgFloats = bf;
  store.bgInts = bi;
  store.flags = fl;
}

export function appendMtext(
  store: CompactMtextStore,
  table: SharedStringTable,
  entity: MtextEntity,
): number {
  const index = store.count;
  ensureMtextCapacity(store, index + 1);
  store.count++;
  const f = index * MTEXT_FLOATS;
  store.floats[f + 0] = entity.insertionPoint.x;
  store.floats[f + 1] = entity.insertionPoint.y;
  store.floats[f + 2] = entity.nominalHeight;
  store.floats[f + 3] = entity.referenceWidth;
  store.floats[f + 4] = entity.rotation;
  store.floats[f + 5] = entity.lineSpacingFactor;
  store.floats[f + 6] = entity.columnWidth;
  store.floats[f + 7] = entity.columnGutter;
  const ii = index * MTEXT_INTS;
  store.ints[ii + 0] = internString(table, entity.styleName);
  store.ints[ii + 1] = internString(table, entity.contents);
  store.ints[ii + 2] = entity.attachmentPoint & 0xffff;
  store.ints[ii + 3] = entity.drawingDirection & 0xffff;

  store.bgFloats[index * MTEXT_BG_FLOATS + 0] = entity.fillBoxScale;
  const bi = index * MTEXT_BG_INTS;
  store.bgInts[bi + 0] = entity.backgroundFillSetting & 0xffff;
  store.bgInts[bi + 1] = entity.backgroundFillColorACI & 0xffff;
  store.bgInts[bi + 2] = entity.backgroundTransparency & 0xffff;
  store.bgInts[bi + 3] = entity.columnType & 0xffff;

  let fl = 0;
  if (entity.backgroundFill) fl |= FLAG_BG;
  if (entity.columnFlowReversed) fl |= FLAG_COL_FLOW;
  if (entity.columnAutoheight) fl |= FLAG_COL_AUTO;
  store.flags[index] = fl;

  writeBase(store.base, table, index, entity);

  if (entity.backgroundFillColor !== undefined) {
    store.backgroundFillColors.set(index, entity.backgroundFillColor);
  }
  if (entity.backgroundFillColorRGB !== undefined) {
    store.backgroundFillColorRGBs.set(index, entity.backgroundFillColorRGB);
  }
  if (entity.columnCount !== 1) {
    store.columnCounts.set(index, entity.columnCount);
  }
  if (entity.columnHeights.length > 0) {
    store.columnHeights.set(index, [...entity.columnHeights]);
  }
  if (entity.lineSpacingStyle !== 1) {
    store.lineSpacingStyles.set(index, entity.lineSpacingStyle);
  }
  if (!isDefaultXAxis(entity.xAxisDirection)) {
    store.xAxisDirections.set(index, { ...entity.xAxisDirection });
  }
  // Codex #342 round-6 P1: persist a NON-default OCS extrusion (the (0,0,−1)
  // MIRRTEXT=1 handedness flag, or any other non-(0,0,1) normal). Reuse the
  // shared `isDefaultExtrusion` so an absent/(0,0,1) extrusion stays absent — no
  // group 210 fabricated on read (matches the schema's `.optional()` extrusion).
  // The explicit `!== undefined` narrows the OPTIONAL MText.extrusion (the TEXT
  // store's extrusion is `.default()`/required, so it needs no narrow).
  if (entity.extrusion !== undefined && !isDefaultExtrusion(entity.extrusion)) {
    store.extrusions.set(index, { ...entity.extrusion });
  }
  if (entity.horizontalWidth !== 0) {
    store.horizontalWidths.set(index, entity.horizontalWidth);
  }
  if (entity.verticalHeight !== 0) {
    store.verticalHeights.set(index, entity.verticalHeight);
  }
  if (entity.annotativeContexts.length > 0) {
    store.annotativeContexts.set(
      index,
      entity.annotativeContexts.map((c) => ({ ...c })),
    );
  }
  if (entity.creationScaleName !== undefined) {
    store.creationScaleNames.set(index, entity.creationScaleName);
  }
  return index;
}

export function mtextFromCompact(
  store: CompactMtextStore,
  table: SharedStringTable,
  index: number,
): MtextEntity {
  if (index < 0 || index >= store.count) {
    throw new RangeError(
      `mtextFromCompact: index ${index} out of range (count=${store.count})`,
    );
  }
  const f = index * MTEXT_FLOATS;
  const ii = index * MTEXT_INTS;
  const bi = index * MTEXT_BG_INTS;
  const fl = store.flags[index] ?? 0;
  const result: MtextEntity = {
    ...readBase(store.base, table, index),
    type: "MText",
    insertionPoint: { x: store.floats[f + 0]!, y: store.floats[f + 1]! },
    nominalHeight: store.floats[f + 2]!,
    referenceWidth: store.floats[f + 3]!,
    attachmentPoint: store.ints[ii + 2]!,
    drawingDirection: store.ints[ii + 3]!,
    contents: lookupString(table, store.ints[ii + 1]!),
    styleName: lookupString(table, store.ints[ii + 0]!),
    rotation: store.floats[f + 4]!,
    lineSpacingStyle: store.lineSpacingStyles.get(index) ?? 1,
    lineSpacingFactor: store.floats[f + 5]!,
    backgroundFill: (fl & FLAG_BG) !== 0,
    columnType: store.bgInts[bi + 3]!,
    columnCount: store.columnCounts.get(index) ?? 1,
    xAxisDirection:
      store.xAxisDirections.get(index) ?? { ...DEFAULT_X_AXIS },
    backgroundFillSetting: store.bgInts[bi + 0]!,
    fillBoxScale: store.bgFloats[index * MTEXT_BG_FLOATS + 0]!,
    backgroundFillColorACI: store.bgInts[bi + 1]!,
    backgroundTransparency: store.bgInts[bi + 2]!,
    columnFlowReversed: (fl & FLAG_COL_FLOW) !== 0,
    columnAutoheight: (fl & FLAG_COL_AUTO) !== 0,
    columnWidth: store.floats[f + 6]!,
    columnGutter: store.floats[f + 7]!,
    columnHeights: store.columnHeights.get(index) ?? [],
    horizontalWidth: store.horizontalWidths.get(index) ?? 0,
    verticalHeight: store.verticalHeights.get(index) ?? 0,
    annotativeContexts: store.annotativeContexts.get(index) ?? [],
  };
  // Codex #342 round-6 P1: restore the optional extrusion CONDITIONALLY so an
  // MTEXT with no group 210 round-trips to exactly `extrusion: undefined` (NOT a
  // defaulted (0,0,1)) — the MText schema field is `.optional()`, and the
  // default-MText `toEqual` round-trip test expects no `extrusion` key. A
  // mirrored MTEXT's (0,0,−1) (or any non-default normal) is restored verbatim.
  const ext = store.extrusions.get(index);
  if (ext !== undefined) result.extrusion = ext;
  const bgC = store.backgroundFillColors.get(index);
  if (bgC !== undefined) result.backgroundFillColor = bgC;
  const bgRGB = store.backgroundFillColorRGBs.get(index);
  if (bgRGB !== undefined) result.backgroundFillColorRGB = bgRGB;
  const csn = store.creationScaleNames.get(index);
  if (csn !== undefined) result.creationScaleName = csn;
  return result;
}

export function estimateMtextBytesPerEntity(): number {
  return (
    MTEXT_FLOATS * 4 +
    MTEXT_INTS * 2 +
    MTEXT_BG_FLOATS * 4 +
    MTEXT_BG_INTS * 2 +
    1 +
    BASE_BYTES_PER_ENTITY
  );
}
